"use client";

import { useRouter } from "next/navigation";
import Link from "next/link";
import { useQuery, useMutation } from "convex/react";
import { useAuth } from "@clerk/clerk-react";
import { api } from "../../../../convex/_generated/api";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  PluginIcon,
  DownloadIcon,
  StarIcon,
  StarFilledIcon,
  EyeIcon,
  ArrowRightIcon,
} from "@/components/icons";
import type { Id } from "../../../../convex/_generated/dataModel";

export default function StarredPluginsPage() {
  const router = useRouter();
  const { isSignedIn, isLoaded } = useAuth();

  const starredPlugins = useQuery(api.plugins.getStarred);
  const toggleStar = useMutation(api.plugins.toggleStar);

  // Redirect if not signed in
  if (isLoaded && !isSignedIn) {
    router.push("/login?redirect=/plugins/starred");
    return null;
  }

  const handleUnstar = async (pluginId: Id<"plugins">) => {
    await toggleStar({ pluginId });
  };

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <Navbar />

      <main className="flex-1">
        <div className="mx-auto max-w-4xl px-4 py-12 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-yellow-500/10 p-3 text-yellow-500">
              <StarFilledIcon className="h-8 w-8" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-foreground">
                Starred Plugins
              </h1>
              <p className="text-muted">Your favorite plugins in one place</p>
            </div>
          </div>

          {/* Plugin List */}
          <div className="mt-8 space-y-4">
            {starredPlugins === undefined ? (
              [...Array(3)].map((_, i) => (
                <Skeleton key={i} className="h-24" />
              ))
            ) : starredPlugins.length === 0 ? (
              <div className="py-20 text-center">
                <StarIcon className="mx-auto h-12 w-12 text-muted" />
                <h2 className="mt-4 text-xl font-semibold text-foreground">
                  No starred plugins
                </h2>
                <p className="mt-2 text-muted">
                  Star plugins to save them here for quick access!
                </p>
                <Link href="/plugins" className="mt-6 inline-block">
                  <Button>Browse Plugins</Button>
                </Link>
              </div>
            ) : (
              starredPlugins.map((plugin) => (
                <Card
                  key={plugin._id}
                  className="group transition-all hover:border-foreground/15 hover:bg-foreground/[0.02]"
                >
                  <CardContent className="flex items-center justify-between p-4">
                    <div className="flex items-center gap-4">
                      <div className="rounded-lg bg-primary/10 p-3 text-primary">
                        <PluginIcon className="h-8 w-8" />
                      </div>
                      <div>
                        <Link
                          href={`/plugins/${plugin.slug}`}
                          className="text-lg font-semibold text-foreground hover:text-primary"
                        >
                          {plugin.name}
                        </Link>
                        <p className="text-sm text-muted">
                          v{plugin.version} &bull; by {plugin.uploaderName}
                        </p>
                        <p className="mt-1 line-clamp-1 text-sm text-muted">
                          {plugin.shortDescription}
                        </p>
                        <div className="mt-2 flex items-center gap-4 text-xs text-muted">
                          <span className="flex items-center gap-1">
                            <EyeIcon className="h-3 w-3" />
                            {plugin.viewCount}
                          </span>
                          <span className="flex items-center gap-1">
                            <DownloadIcon className="h-3 w-3" />
                            {plugin.downloadCount}
                          </span>
                          <span className="flex items-center gap-1">
                            <StarIcon className="h-3 w-3" />
                            {plugin.starCount}
                          </span>
                        </div>
                        {plugin.tags.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-1">
                            {plugin.tags.slice(0, 3).map((tag) => (
                              <Badge key={tag} variant="secondary" className="text-xs">
                                {tag}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleUnstar(plugin._id)}
                        className="text-yellow-500 hover:bg-yellow-500/10"
                      >
                        <StarFilledIcon className="mr-1 h-4 w-4" />
                        Unstar
                      </Button>
                      <Link href={`/plugins/${plugin.slug}`}>
                        <Button size="sm">
                          View
                          <ArrowRightIcon className="ml-1 h-4 w-4" />
                        </Button>
                      </Link>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>

          {/* Back Button */}
          <div className="mt-8">
            <Link href="/plugins">
              <Button variant="outline">
                <ArrowRightIcon className="mr-2 h-4 w-4 rotate-180" />
                Back to Plugins
              </Button>
            </Link>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
